import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useSubscription, useSubscriptionPlans } from '../hooks/useSubscription';
import API_BASE_URL from '../config';
import { 
  isIAPAvailable, 
  initializeIAP, 
  purchaseSubscription,
  areProductsAvailable,
  getReceiptData,
  validateReceipt,
  refreshReceipt
} from '../services/iapService';
import RestorePurchasesButton from './RestorePurchasesButton';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/plansPage.css';

interface PlansPageProps {
  onClose?: () => void;
}

const PlansPage: React.FC<PlansPageProps> = ({ onClose }) => {
  const { tierInfo, usage, formatLimit, formatStorage } = useSubscription();
  const { plans, loading } = useSubscriptionPlans();
  const [upgradingTier, setUpgradingTier] = useState<string | null>(null);
  const [iapReady, setIapReady] = useState(false);
  const [productsAvailable, setProductsAvailable] = useState(false);
  
  // Check if running on iOS native app
  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  // Initialize IAP on iOS
  useEffect(() => {
    if (isIAPAvailable()) {
      initializeIAP()
        .then(() => {
          setIapReady(true);
          setProductsAvailable(areProductsAvailable());
          console.log('IAP initialized successfully');
        })
        .catch((error) => {
          console.error('IAP initialization failed:', error);
        });
    }
  }, []);

  // Listen for auto-activation events from IAP service
  useEffect(() => {
    const handleAutoActivate = async (event: any) => {
      const { productId } = event.detail;
      console.log('[PlansPage] Auto-activation triggered for:', productId);
      
      const tierMap: Record<string, string> = {
        'com.wanderingsock.annual.basic': 'basic',
        'com.wanderingsock.annual.pro': 'pro',
        'com.wanderingsock.annual.business': 'business'
      };
      
      const tier = tierMap[productId];
      if (!tier) {
        console.error('[PlansPage] Unknown product ID:', productId);
        return;
      }
      
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        console.log('[PlansPage] Auto-activating tier:', tier, 'token exists:', !!token);
        
        const response = await fetch(`${API_BASE_URL}/api/subscription/sandbox-activate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ productId, tier })
        });
        
        console.log('[PlansPage] Auto-activation response status:', response.status);
        
        if (response.ok) {
          alert(`Subscription activated: ${tier.toUpperCase()}`);
          window.location.reload();
        } else {
          const errorText = await response.text();
          console.error('[PlansPage] Auto-activation failed:', errorText);
        }
      } catch (error) {
        console.error('[PlansPage] Auto-activation error:', error);
      }
    };
    
    window.addEventListener('iap-auto-activate', handleAutoActivate);
    return () => window.removeEventListener('iap-auto-activate', handleAutoActivate);
  }, []);

  if (loading) {
    return (
      <div className="plans-page">
        <div className="plans-loading">Loading plans...</div>
      </div>
    );
  }

  const currentTier = tierInfo?.tier || 'free';

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      free: '#6c757d',
      basic: '#007bff',
      pro: '#6f42c1',
      business: '#fd7e14',
    };
    return colors[tier] || '#007bff';
  };

  const handleUpgrade = async (tier: string) => {
    if (upgradingTier) return;

    try {
      setUpgradingTier(tier);

      // iOS IAP flow
      if (isIOS && iapReady) {
        // Set up listener for purchase completion
        const handlePurchaseFinished = async (event: any) => {
          console.log('Purchase finished event received:', event.detail);
          
          // Wait longer for receipt to be available (iOS can be slow in sandbox)
          setTimeout(async () => {
            console.log('[PlansPage] Purchase finished, attempting to retrieve receipt data...');
            
            // First try to refresh the receipt from Apple
            console.log('[PlansPage] Refreshing receipt from Apple...');
            await refreshReceipt();
            
            console.log('[PlansPage] Getting receipt data...');
            const { receipt, debugInfo } = await getReceiptData();
            console.log('[PlansPage] Receipt result:', receipt ? 'Found' : 'Not found');
            
            if (receipt) {
              console.log('Receipt found! Length:', receipt.length);
              console.log('Validating receipt with backend...');
              const validation = await validateReceipt(receipt);
              
              if (validation.success) {
                alert('Subscription activated successfully!');
                window.location.reload();
              } else {
                console.error('Receipt validation failed:', validation.error);
                alert('Purchase successful but validation failed. Please contact support.');
              }
            } else {
              console.error('No receipt data available after purchase');
              console.error('Debug info:', debugInfo);
              
              // Sandbox workaround: Check if Apple confirms ownership and activate directly
              const { CdvPurchase } = window;
              if (CdvPurchase) {
                const { store } = CdvPurchase;
                const products = [
                  store.get('com.wanderingsock.annual.basic'),
                  store.get('com.wanderingsock.annual.pro'),
                  store.get('com.wanderingsock.annual.business'),
                ].filter(Boolean);
                
                console.log('[PlansPage] Checking ownership after purchase:', products.map(p => ({
                  id: p.id,
                  owned: p.owned,
                  state: p.state
                })));
                
                const ownedProduct = products.find(p => p.owned);
                if (ownedProduct) {
                  console.log('[PlansPage] Apple confirms ownership of:', ownedProduct.id);
                  
                  const tierMap: Record<string, string> = {
                    'com.wanderingsock.annual.basic': 'basic',
                    'com.wanderingsock.annual.pro': 'pro',
                    'com.wanderingsock.annual.business': 'business'
                  };
                  
                  const tier = tierMap[ownedProduct.id];
                  
                  // Activate via sandbox endpoint
                  try {
                    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
                    console.log('[PlansPage] Calling sandbox-activate with tier:', tier, 'token exists:', !!token);
                    
                    const response = await fetch(`${API_BASE_URL}/api/subscription/sandbox-activate`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        productId: ownedProduct.id,
                        tier: tier
                      })
                    });
                    
                    console.log('[PlansPage] Sandbox activation response status:', response.status);

                    if (response.ok) {
                      alert(`Subscription activated: ${tier.toUpperCase()} (sandbox mode)`);
                      window.location.reload();
                      return;
                    } else {
                      const errorText = await response.text();
                      console.error('[PlansPage] Sandbox activation failed:', errorText);
                      alert('Failed to activate subscription. Please try "Restore Purchases" button.');
                      return;
                    }
                  } catch (error) {
                    console.error('[PlansPage] Sandbox activation error:', error);
                    alert('Network error during activation. Please try "Restore Purchases" button.');
                    return;
                  }
                } else {
                  console.log('[PlansPage] No owned products found, showing debug alert');
                }
              } else {
                console.log('[PlansPage] CdvPurchase not available');
              }
              
              alert('Purchase completed but no receipt found. Please try "Restore Purchases" button.');
            }
            
            setUpgradingTier(null);
          }, 2000);
          
          // Remove listener
          window.removeEventListener('iap-purchase-finished', handlePurchaseFinished);
        };
        
        window.addEventListener('iap-purchase-finished', handlePurchaseFinished);
        
        // Initiate purchase
        const result = await purchaseSubscription(tier as 'basic' | 'pro' | 'business');
        
        if (!result.success) {
          console.error('Purchase failed:', result.error);
          alert(result.error || 'Purchase failed. Please try again.');
          window.removeEventListener('iap-purchase-finished', handlePurchaseFinished);
          return;
        }
        
        // Purchase initiated - waiting for completion event
        console.log('Purchase initiated, waiting for completion...');
        return;
      }

      // Web Stripe flow
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('Upgrade requires authentication');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ tier }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('Failed to create checkout session', data);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else if (data.sessionId) {
        console.warn('Checkout session created without URL', data.sessionId);
      }
    } catch (e) {
      console.error('Error starting upgrade:', e);
      alert('An error occurred. Please try again.');
    } finally {
      setUpgradingTier(null);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="plans-overlay" onClick={onClose} />
      
      {/* Bottom Sheet */}
      <div className="plans-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="plans-bottom-sheet-handle" />
        {onClose && (
          <ModalCloseButton onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }} />
        )}

      <div className="plans-header">
        <h1>Choose Your Plan</h1>
        <p className="plans-subtitle">
          Upgrade anytime. Cancel anytime. No hidden fees.
        </p>
        {isIOS && iapReady && (
          <div style={{ marginTop: '12px' }}>
            <RestorePurchasesButton />
          </div>
        )}
      </div>

      {/* Current Plan Banner */}
      {tierInfo && (
        <div className="current-plan-banner">
          <div className="current-plan-label">Your Current Plan</div>
          <div className="current-plan-name">{tierInfo.display_name}</div>
          {usage && (
            <div className="current-plan-usage">
              <span>{usage.items.used} / {formatLimit(usage.items.limit)} items</span>
              <span className="usage-separator">•</span>
              <span>{usage.ai_scans_per_month.used} / {formatLimit(usage.ai_scans_per_month.limit)} AI scans</span>
            </div>
          )}
        </div>
      )}

      {/* Plans Grid */}
      <div className="plans-grid">
        {plans.map((plan) => {
          const isCurrentPlan = plan.tier === currentTier;
          const isFree = plan.tier === 'free';
          const tierColor = getTierColor(plan.tier);
          const showPrice = !isFree || !isCurrentPlan;
          const description = isFree
            ? 'Perfect for getting started'
            : plan.description;

          return (
            <div
              key={plan.tier}
              className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}
              style={{ '--tier-color': tierColor } as React.CSSProperties}
            >
              {isCurrentPlan && (
                <div className="current-badge">Current Plan</div>
              )}

              <div className="plans-card-header">
                <h3 className="plan-name">{plan.display_name}</h3>
                <p className="plan-description">{description}</p>
                
                {/* Billed Amount - Most Prominent Element per Apple Guideline 3.1.2 */}
                {!isFree && (
                  <div className="plan-price-primary">
                    <div className="price-combined">
                      <span className="price-dollars">${(plan.price_cents / 100).toFixed(2)}</span>
                      <span className="price-slash-year">/year</span>
                    </div>
                    <div className="price-billed-label">Billed ${(plan.price_cents / 100).toFixed(2)} per year. Auto-renews annually.</div>
                  </div>
                )}
                {isFree && (
                  <div className="plan-price-primary">
                    <div className="price-free">Free</div>
                  </div>
                )}
                
                {/* Subscription Details - Subordinate */}
                {!isFree && (
                  <div className="plan-subscription-details">
                    <div className="subscription-term">Auto-renewable annual subscription</div>
                  </div>
                )}
              </div>

              <div className="plan-features">
                <div className="feature-item">
                  <span className="feature-icon">📦</span>
                  <span className="feature-text">
                    {plan.item_limit === -1 ? 'Unlimited' : formatLimit(plan.item_limit)} items
                  </span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🤖</span>
                  <span className="feature-text">
                    {plan.ai_scan_limit_per_month === -1 ? 'Unlimited' : formatLimit(plan.ai_scan_limit_per_month)} AI scans/month
                  </span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">☁️</span>
                  <span className="feature-text">
                    {formatStorage(plan.storage_limit_mb)} storage
                  </span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🤝</span>
                  <span className="feature-text">
                    {plan.shared_collections_limit === -1 ? 'Unlimited' : formatLimit(plan.shared_collections_limit)} shared collections
                  </span>
                </div>
                {plan.groups_limit > 0 && (
                  <div className="feature-item">
                    <span className="feature-icon">👥</span>
                    <span className="feature-text">
                      {plan.groups_limit === -1 ? 'Unlimited' : formatLimit(plan.groups_limit)} groups
                    </span>
                  </div>
                )}

                {/* Feature badges */}
                <div className="feature-badges">
                  {plan.tier === 'pro' && isIOS && (
                    <span className="feature-badge">👨‍👩‍👧‍👦 Family Sharing (up to 6)</span>
                  )}
                  {plan.features.includes('insurance_docs') && (
                    <span className="feature-badge">📄 Insurance Docs</span>
                  )}
                  {plan.features.includes('advanced_export') && (
                    <span className="feature-badge">📊 Advanced Export</span>
                  )}
                </div>
              </div>

              <div className="plan-action">
                {isCurrentPlan ? (
                  <button className="plan-btn current" disabled>
                    Current Plan
                  </button>
                ) : isFree ? (
                  <button className="plan-btn free" disabled>
                    Free Forever
                  </button>
                ) : isIOS && !iapReady ? (
                  <button className="plan-btn ios-coming-soon" disabled>
                    Loading...
                  </button>
                ) : isIOS && iapReady && !productsAvailable ? (
                  <button className="plan-btn ios-unavailable" disabled>
                    Temporarily Unavailable
                  </button>
                ) : (
                  <button
                    className="plan-btn upgrade"
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={!!upgradingTier}
                  >
                    {upgradingTier === plan.tier
                      ? 'Starting upgrade…'
                      : `Upgrade to ${plan.display_name}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ Section */}
      <div className="plans-faq">
        <h3>Frequently Asked Questions</h3>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I change plans anytime?</h4>
            <p>Yes! Upgrades take effect immediately. Downgrades take effect at your next billing cycle.</p>
          </div>
          <div className="faq-item">
            <h4>What happens to my data if I downgrade?</h4>
            <p>Your data is safe. You'll just be limited to the new tier's limits for new items.</p>
          </div>
          {!isIOS && (
            <div className="faq-item">
              <h4>Do you offer refunds?</h4>
              <p>Yes, we offer a 30-day money-back guarantee on all paid plans.</p>
            </div>
          )}
        </div>
      </div>


      {/* Legal Links - Required by Apple App Store for Subscriptions */}
      <div className="plans-legal">
        <p className="legal-text">
          By subscribing, you agree to our{' '}
          <a href="/help/terms" target="_blank" rel="noopener noreferrer" className="legal-link">
            Terms of Use
          </a>
          {' '}and{' '}
          <a href="/help/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">
            Privacy Policy
          </a>
          .
        </p>
        {isIOS && (
          <p className="legal-text ios-note">
            Subscriptions are charged to your Apple ID account. Auto-renews unless cancelled at least 24 hours before the end of the current period. Manage subscriptions in your Apple ID settings.
          </p>
        )}
      </div>
      </div>
    </>
  );
};

export default PlansPage;
